import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { type OCRScopeWork } from '@/features/jobs/scoping/OCRScopeWork'
import { type OCRScopeWorkStore } from '@/features/jobs/scoping/OCRScopeWorkStore'
import { type Id } from '@/infra/id'
import { type OCRScopingJobsPermissionViewModel } from '@/features/jobs/scoping/OCRScopingJobsPermissionViewModel'
import type { AlertFactory } from '@/features/shell'
import { type DraftJobStore } from '@/features/jobs/drafting/DraftJobStore'
import { OCRConfirmBulkSubmitDialogViewModel } from '@/features/jobs/scoping/components/OCRConfirmBulkSubmitDialog/OCRConfirmBulkSubmitDialogViewModel'
import { OCRConfirmBulkReviewDialogViewModel } from '@/features/jobs/scoping/components/OCRConfirmBulkReviewDialog/OCRConfirmBulkReviewDialogViewModel'
import { OCRConfirmBulkRerunDialogViewModel } from '@/features/jobs/scoping/components/OCRConfirmBulkRerunDialog/OCRConfirmBulkRerunDialogViewModel'
import { OCRBulkProgressDialogViewModel } from '@/features/jobs/scoping/components/OCRBulkProgressDialog/OCRBulkProgressDialogViewModel'
import { formatOCRScopeWorkStatus } from './ocr-scope-work-formatters'
import { type OCRScopeWorkStatusFilter } from './OCRScopingJobSidePanelViewModel'
import { type InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'

/**
 * Selects scope works and runs bulk actions on them.
 */
export class OCRScopingJobBulkActionViewModel {
  readonly confirmBulkSubmitDialogVm = new OCRConfirmBulkSubmitDialogViewModel()

  readonly confirmBulkReviewDialogVm = new OCRConfirmBulkReviewDialogViewModel()

  readonly confirmBulkRerunDialogVm = new OCRConfirmBulkRerunDialogViewModel()

  readonly bulkProgressDialogVm = new OCRBulkProgressDialogViewModel()

  @observable accessor selectedStatusFilter: OCRScopeWorkStatusFilter | null =
    null

  @observable accessor itemsSelectedForBulkAction = new Map<Id, OCRScopeWork>()

  constructor(
    private readonly _ocrScopingJobsPermissionVm: OCRScopingJobsPermissionViewModel,
    private readonly _ocrScopeWorkStore: OCRScopeWorkStore,
    private readonly _draftJobStore: DraftJobStore,
    private readonly _scroller: InfiniteScrollProvider<OCRScopeWork>,
    private readonly _newAlert: AlertFactory,
  ) {}

  @computed
  get canViewScopingJobs() {
    return this._ocrScopingJobsPermissionVm.canViewScopingJobs
  }

  @computed
  get canManageOCRScopingJobs() {
    return this._ocrScopingJobsPermissionVm.canManageOCRScopingJobs
  }

  @computed
  get hasAnyItemsSelectedForBulkAction() {
    return this.itemsSelectedForBulkAction.size > 0
  }

  @computed
  get hasAllItemsSelectedForBulkAction() {
    return this.itemsSelectedForBulkAction.size === this._scroller.items.length
  }

  @computed
  get hasSomeItemsSelectedForBulkAction() {
    return (
      this.itemsSelectedForBulkAction.size > 0 &&
      this.itemsSelectedForBulkAction.size < this._scroller.items.length
    )
  }

  /**
   * The name a scoping job is listed with.
   */
  itemName(item: OCRScopeWork) {
    return item.jobName === '' ? item.fileName : item.jobName
  }

  /**
   * The status a scoping job is listed with, with the confidence level while
   * it may need a review.
   */
  itemSubtitle(item: OCRScopeWork) {
    const status = formatOCRScopeWorkStatus(item.status)
    return this.canManageOCRScopingJobs && mayNeedReview(item)
      ? `${status} (${item.confidenceLevel}% confidence)`
      : status
  }

  @action.bound
  toggleItemForBulkAction(item: OCRScopeWork) {
    if (this.itemsSelectedForBulkAction.has(item.id)) {
      this.itemsSelectedForBulkAction.delete(item.id)
      return
    }

    this.itemsSelectedForBulkAction.set(item.id, item)
  }

  @action.bound
  deselectItem(item: OCRScopeWork) {
    this.itemsSelectedForBulkAction.delete(item.id)
  }

  @action.bound
  clearItemsSelectedForBulkAction() {
    this.itemsSelectedForBulkAction.clear()
  }

  @action.bound
  toggleSelectAllItemForBulkAction() {
    if (
      this.hasSomeItemsSelectedForBulkAction ||
      this.hasAllItemsSelectedForBulkAction
    ) {
      this.clearItemsSelectedForBulkAction()
      return
    }

    this._scroller.items.forEach((item) => {
      this.itemsSelectedForBulkAction.set(item.id, item)
    })
  }

  @action.bound
  setStatusFilter(statusFilter: OCRScopeWorkStatusFilter | null) {
    this.selectedStatusFilter = statusFilter
    this.clearItemsSelectedForBulkAction()
  }

  /**
   * Asks the user to confirm, then submits the selected jobs.
   */
  @action.bound
  async confirmBulkSubmit() {
    if (await this.confirmBulkSubmitDialogVm.prompt(this._promptOptions())) {
      await this.bulkSubmit()
    }
  }

  /**
   * Asks the user to confirm, then sets the selected scoping jobs as ready to
   * submit.
   */
  @action.bound
  async confirmBulkReview() {
    if (await this.confirmBulkReviewDialogVm.prompt(this._promptOptions())) {
      await this.bulkReview()
    }
  }

  /**
   * Asks the user to confirm, then reruns OCR for the selected scoping jobs.
   */
  @action.bound
  async confirmBulkRerun() {
    if (await this.confirmBulkRerunDialogVm.prompt(this._promptOptions())) {
      await this.bulkRerun()
    }
  }

  bulkSubmit = task.resolved(async () => {
    if (!this.canViewScopingJobs) {
      return
    }

    const { succeeded, failed } = await this._process(async (item) => {
      await this._draftJobStore
        .upgradeLegendVersion(item.jobId)
        // Submitting on the current legend version still works, so a failed
        // upgrade must not block the submission.
        .catch(() => console.error('Failed to upgrade legend', item.jobId))
      const submissionId = await this._draftJobStore.submit({
        id: item.jobId,
      })
      await this._draftJobStore.waitForSubmissionOutcome({ submissionId })
    })

    if (succeeded > 0) {
      this._newAlert()
        .saying('Success!', `${succeeded} jobs were submitted successfully.`)
        .withState('success')
        .autoDismiss()
        .show()
    }

    if (failed > 0) {
      this._newAlert()
        .saying('Error!', `${failed} jobs failed submission.`)
        .withState('error')
        .autoDismiss()
        .show()
    }
  })

  bulkReview = task.resolved(async () => {
    if (
      !this.canManageOCRScopingJobs ||
      this.selectedStatusFilter?.id !== 'UnderReview'
    ) {
      return
    }

    const { succeeded, failed } = await this._process((item) =>
      this._ocrScopeWorkStore.review(item.id),
    )

    if (succeeded > 0) {
      this._newAlert()
        .saying(
          'Success!',
          `${succeeded} scoping jobs were moved to ready to submit.`,
        )
        .withState('success')
        .autoDismiss()
        .show()
    }

    if (failed > 0) {
      this._newAlert()
        .saying(
          'Error!',
          `${failed} scoping jobs failed while trying to set them as ready to submit.`,
        )
        .withState('error')
        .autoDismiss()
        .show()
    }
  })

  bulkRerun = task.resolved(async () => {
    if (
      !this.canManageOCRScopingJobs ||
      this.selectedStatusFilter?.id !== 'ErrorOccurred'
    ) {
      return
    }

    const { succeeded, failed } = await this._process((item) =>
      this._ocrScopeWorkStore.rerun(item.id),
    )

    if (succeeded > 0) {
      this._newAlert()
        .saying('Success!', `${succeeded} scoping job were sent to rerun OCR.`)
        .withState('success')
        .autoDismiss()
        .show()
    }

    if (failed > 0) {
      this._newAlert()
        .saying(
          'Error!',
          `${failed} scoping jobs failed while trying to trigger the rerun.`,
        )
        .withState('error')
        .autoDismiss()
        .show()
    }
  })

  private _promptOptions(): PromptBulkActionOptions {
    return { selectedCount: this.itemsSelectedForBulkAction.size }
  }

  private _process(request: (item: OCRScopeWork) => Promise<unknown>) {
    return this.bulkProgressDialogVm.process(
      Array.from(this.itemsSelectedForBulkAction.values()),
      request,
    )
  }
}

/**
 * Options for prompting about a bulk action.
 */
export interface PromptBulkActionOptions {
  selectedCount: number
  signal?: AbortSignal
}

function mayNeedReview({ engineStatus, jobStatus }: OCRScopeWork) {
  return (
    (engineStatus === 'Completed' &&
      jobStatus !== 'ReadyToSubmit' &&
      jobStatus !== 'Submitted') ||
    engineStatus === 'ReviewRequired' ||
    engineStatus === 'ReviewSuggested'
  )
}
